import { Head, Link, usePage } from "@inertiajs/react"
import { Volume2, ArrowLeft, ArrowRight, Sparkles } from "lucide-react"
import { useMemo } from "react"
import camelcaseKeys from "camelcase-keys"
import AudioPlayer from "@/components/conversation/audio-player"
import { CommentSection } from "@/components/phrases/comment-section"
import { GuruExplanationCard } from "@/components/phrases/guru-explanation-card"
import { HawkReviewCard } from "@/components/phrases/hawk-review-card"
import { SharePopover } from "@/components/share-popover"
import { MarketingNav } from "@/components/marketing-nav"
import { MarketingFooter } from "@/components/marketing-footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { usePrefetchLink } from "@/hooks/use-prefetch-link"
import { cn } from "@/lib/utils"
import type { Comment, Phrase } from "@/types/phrase"

interface SeoData {
  title: string
  description: string
  imageUrl: string
  structuredData: Record<string, unknown>[]
}

interface Props {
  phrase: Phrase
  comments?: Comment[]
  relatedPhrases?: Phrase[]
  canonicalUrl?: string
  seo?: SeoData
}

// Helper to extract text from potentially nested object
const extractText = (value: unknown): string => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object" && value !== null && "text" in value) {
    return (value as { text: string }).text || ""
  }
  return ""
}

export default function PhraseShow(props: unknown) {
  const page = usePage()
  const { auth } = page.props

  const rawData = useMemo(
    () => camelcaseKeys(props as Record<string, unknown>, { deep: true }) as unknown as Props,
    [props]
  )

  // Normalize phrase fields that might be objects
  const phrase = useMemo(() => ({
    ...rawData.phrase,
    thaiText: extractText(rawData.phrase?.thaiText),
    romanization: extractText(rawData.phrase?.romanization),
  }), [rawData.phrase])

  const { comments = [], relatedPhrases = [], seo, canonicalUrl } = rawData

  // Prefetch links
  const dashboardPrefetch = usePrefetchLink("/dashboard")
  const signUpPrefetch = usePrefetchLink("/sign_up")
  const howItWorksPrefetch = usePrefetchLink("/how-it-works")

  const shareUrl = typeof window !== "undefined" ? window.location.href : ""

  return (
    <>
      {seo && (
        <Head title={seo.title}>
          <meta name="description" content={seo.description} />
          <meta name="robots" content="index, follow" />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

          {/* Open Graph */}
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:type" content="article" />
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          <meta property="og:image" content={seo.imageUrl} />

          {/* Twitter */}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          <meta name="twitter:image" content={seo.imageUrl} />

          {/* JSON-LD */}
          <script type="application/ld+json">
            {JSON.stringify(seo.structuredData)}
          </script>
        </Head>
      )}

      <MarketingNav />

      {/* Main Content */}
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {/* Hero section with phrase */}
        <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30">
          {/* Decorative background elements */}
          <div className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-indigo-500/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 size-72 rounded-full bg-violet-500/5 blur-3xl" />

          <div className="relative mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
            {/* Breadcrumb */}
            <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Link
                href="/"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Home
              </Link>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <Link
                href="/phrases"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Phrases
              </Link>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <span className="font-medium text-foreground line-clamp-1">
                {phrase.content}
              </span>
            </nav>

            {/* Back link */}
            <Link
              href="/phrases"
              className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
            >
              <ArrowLeft className="size-4" />
              All Phrases
            </Link>

            {/* Main Phrase Card */}
            <Card className="overflow-hidden border-slate-200 bg-white shadow-xl shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
              {/* Header with English phrase */}
              <div className="border-b border-slate-100 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50 sm:px-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Badge variant="secondary" className="mb-3 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                      How to say in Thai
                    </Badge>
                    <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
                      {phrase.content}
                    </h1>
                  </div>
                  <SharePopover
                    url={shareUrl}
                    title={`"${phrase.content}" in Thai`}
                    description={phrase.romanization ? `${phrase.romanization} — ${phrase.thaiText}` : phrase.thaiText}
                  />
                </div>
              </div>

              {/* Romanization hero + Thai text secondary (matches flashcards) */}
              <CardContent className="p-8 sm:p-12">
                <div className="text-center">
                  {/* Romanization - the hero learners focus on */}
                  {phrase.romanization && (
                    <p className="mb-4 text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl lg:text-6xl">
                      {phrase.romanization}
                    </p>
                  )}

                  {/* Thai text - secondary reference */}
                  <p className="mb-8 text-2xl text-slate-600 dark:text-slate-400">
                    {phrase.thaiText}
                  </p>

                  {/* Audio player */}
                  {phrase.audioUrl && (
                    <div className="mx-auto w-full max-w-md">
                      <AudioPlayer
                        audioUrl={phrase.audioUrl}
                        variant="dark"
                        className="w-full px-4 py-3"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Content section */}
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Guru Explanation - SEO-rich linguistic breakdown (with Hawk verification) */}
          {phrase.guruExplanation?.explanation && (
            <GuruExplanationCard
              explanation={phrase.guruExplanation}
              hawkReview={phrase.hawkReview}
              className="mb-6"
            />
          )}

          {/* Hawk Review - Detailed peer review */}
          {phrase.hawkReview && (
            <HawkReviewCard
              review={phrase.hawkReview}
              className="mb-10"
            />
          )}

          {/* Editorial Content - Teacher context */}
          {(phrase.editorialNote || phrase.editorsAudioUrl) && (
            <Card className="mb-10 border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-800/30">
              <CardContent className="p-6 sm:p-8 space-y-6">
                {phrase.editorialNote && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Editor's Note
                    </h3>
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none text-slate-600 dark:text-slate-400"
                      dangerouslySetInnerHTML={{ __html: phrase.editorialNote }}
                    />
                  </div>
                )}
                {phrase.editorsAudioUrl && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Editor's Audio
                    </h3>
                    <div className="w-full max-w-md">
                      <AudioPlayer
                        audioUrl={phrase.editorsAudioUrl}
                        variant="dark"
                        className="w-full px-4 py-3"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tips section */}
          <Card className="mb-10 border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <CardContent className="p-6 sm:p-8">
              <h2 className="mb-5 flex items-center gap-3 text-lg font-semibold text-foreground">
                <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/50">
                  <Volume2 className="size-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                Pronunciation Tips
              </h2>
              <ul className="space-y-4 text-slate-600 dark:text-slate-400">
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-indigo-500" />
                  <span>
                    Listen to the audio multiple times to hear the natural rhythm and tones.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-indigo-500" />
                  <span>
                    Thai is a tonal language - pay attention to the rise and fall of pitch.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-indigo-500" />
                  <span>
                    Practice speaking slowly at first, then gradually increase your speed.
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Comments */}
          <CommentSection
            phraseSlug={phrase.slug}
            comments={comments}
            currentUser={
              auth.user
                ? {
                  id: auth.user.id,
                  name: auth.user.name,
                  role: (auth.user.role as "learner" | "teacher") ?? "learner",
                }
                : null
            }
            className="mb-10"
          />

          {/* Related Phrases */}
          {relatedPhrases.length > 0 && (
            <div className="mb-10">
              <h2 className="mb-6 text-xl font-bold text-foreground">
                Related Phrases
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {relatedPhrases.slice(0, 4).map((related, index) => (
                  <Link
                    key={related.id}
                    href={`/phrases/${related.slug}`}
                    className="group"
                  >
                    <Card
                      className={cn(
                        "h-full border-slate-200 bg-white transition-all duration-300",
                        "hover:-translate-y-1 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-500/10",
                        "dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-700",
                        "animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CardContent className="p-5">
                        <p className="mb-2 font-semibold text-foreground transition-colors group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                          {related.content}
                        </p>
                        {/* Romanization hero */}
                        {related.romanization && (
                          <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                            {related.romanization}
                          </p>
                        )}
                        {/* Thai text secondary */}
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          {related.thaiText}
                        </p>
                        <div className="mt-3 flex items-center gap-1 text-xs font-medium text-indigo-600 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-indigo-400">
                          <span>Learn this phrase</span>
                          <ArrowRight className="size-3" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              {relatedPhrases.length > 4 && (
                <div className="mt-6 text-center">
                  <Link
                    href="/phrases"
                    className="text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    View all phrases →
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* CTA Section */}
          <Card className="relative overflow-hidden border-slate-200 dark:border-slate-700">
            {/* Decorative elements */}
            <div className="pointer-events-none absolute -top-12 -right-12 size-48 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-12 size-48 rounded-full bg-violet-500/10 blur-3xl" />

            <CardContent className="relative bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-8 text-center dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30 sm:p-12">
              <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/50">
                <Sparkles className="size-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
                Ready to speak Thai confidently?
              </h2>
              <p className="mx-auto mb-8 max-w-xl text-slate-600 dark:text-slate-400">
                Practice with AI-powered conversations and remember phrases forever
                with spaced repetition flashcards.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Button asChild size="lg" className="gap-2 shadow-lg shadow-indigo-500/20">
                  <Link
                    href={auth.user ? "/dashboard" : "/sign_up"}
                    {...(auth.user ? dashboardPrefetch : signUpPrefetch)}
                  >
                    <Sparkles className="size-5" />
                    {auth.user ? "Go to Dashboard" : "Start Learning Free"}
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/how-it-works" {...howItWorksPrefetch}>
                    See How It Works
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
